import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  getMatchingList,
  getMatchingListCount,
} from "firebase_/firebase_function/matching";
import MatchingItemMobile from "components/repair/mobile/matchingItem_m";
import FilterListMenu from "components/repair/filterListMenu";
import { useUser } from "util/context/user_context";
import { useSearchParams } from "react-router-dom";

const C = {
  green: "#059669",
  greenDark: "#047857",
  greenBg: "#ECFDF5",
  ink: "#111827",
  body: "#374151",
  sub: "#6B7280",
  line: "#E5E7EB",
  bg: "#F7FAF7",
  amber800: "#92400E",
  blue700: "#1D4ED8",
};

function MiniStat({ label, value, accent }) {
  return (
    <div
      style={{
        flex: 1,
        padding: "10px 12px",
        background: "#fff",
        borderRadius: 10,
        border: `1px solid ${C.line}`,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 2,
        minWidth: 0,
      }}
    >
      <div style={{ fontSize: 11, color: C.sub, fontWeight: 500 }}>{label}</div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 800,
          color: accent || C.ink,
          letterSpacing: "-0.02em",
          lineHeight: 1,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function SkeletonRow() {
  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        padding: 14,
        background: "#fff",
        borderRadius: 12,
        border: `1px solid ${C.line}`,
      }}
    >
      <div style={{ width: 56, height: 56, borderRadius: 10, background: "#F3F4F6", flexShrink: 0 }} />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
        <div style={{ height: 14, width: "70%", background: "#F3F4F6", borderRadius: 5 }} />
        <div style={{ height: 10, width: "50%", background: "#F3F4F6", borderRadius: 5 }} />
      </div>
    </div>
  );
}

export default function MobileMatchingMainScreen() {
  const { userData } = useUser();
  const [searchParams] = useSearchParams();
  const status = searchParams.get("status");
  // '모든 견적' 옵션은 숨김 — 항상 내 요청만 조회한다.
  const id = userData.id;

  const [repairs, setRepairs] = useState([]);
  const [initLoading, setInitLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastCreateDate, setLastCreateDate] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [inProgressCount, setInProgressCount] = useState(0);
  const [doneCount, setDoneCount] = useState(0);
  const observer = useRef();

  // 필터 변경 시 초기화 후 재조회
  useEffect(() => {
    if (Object.keys(userData).length === 0) return;
    setRepairs([]);
    setLastCreateDate("");
    setHasMore(true);
    setInitLoading(true);
    fetchCounts();
    fetchData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, status]);

  const fetchCounts = async () => {
    try {
      // '전체'는 현재 탭(status)과 무관하게 항상 전체 개수를 보여준다.
      const [total, p, d] = await Promise.all([
        getMatchingListCount(id, null),
        getMatchingListCount(id, "0"),
        getMatchingListCount(id, "1"),
      ]);
      setTotalCount(total || 0);
      setInProgressCount(p || 0);
      setDoneCount(d || 0);
    } catch {
      /* noop */
    }
  };

  const fetchData = async (isFirst = false) => {
    if (!isFirst && !hasMore) return;
    setLoading(true);
    const lastDate = isFirst ? "" : lastCreateDate;
    const data = await getMatchingList(id, 10, lastDate, status);
    if (data) {
      const list = data[1] || [];
      setRepairs((prev) => (isFirst ? list : [...prev, ...list]));
      setLastCreateDate(list[list.length - 1]?.createDate || "");
      setHasMore(list.length > 0);
    }
    setLoading(false);
    setInitLoading(false);
  };

  const lastRepairElementRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          fetchData(false);
        }
      });
      if (node) observer.current.observe(node);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading, hasMore],
  );

  return (
    <div
      style={{
        fontFamily:
          'Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, sans-serif',
        padding: "16px 16px 32px",
        background: C.bg,
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 14,
      }}
    >
      <div>
        <div style={{ fontSize: 20, fontWeight: 800, color: C.ink, letterSpacing: "-0.02em" }}>
          요청 목록
        </div>
        <div style={{ marginTop: 2, fontSize: 12, color: C.sub }}>
          등록된 수리요청과 진행 상황
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: "flex", gap: 8 }}>
        <MiniStat label="전체" value={totalCount} accent={C.ink} />
        <MiniStat label="매칭중" value={inProgressCount} accent={C.amber800} />
        <MiniStat label="매칭완료" value={doneCount} accent={C.blue700} />
      </div>

      {/* Filters — '모든 견적' 숨김, 내 요청만 */}
      <FilterListMenu hideScope />

      {/* List */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {initLoading ? (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        ) : repairs.length > 0 ? (
          repairs.map((repair, index) => {
            const isLast = index === repairs.length - 1;
            return (
              <div key={repair?.docId || index} ref={isLast ? lastRepairElementRef : null}>
                <MatchingItemMobile repair={repair} />
              </div>
            );
          })
        ) : (
          <div
            style={{
              padding: "48px 16px",
              background: "#fff",
              borderRadius: 14,
              border: `1px dashed ${C.line}`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
            }}
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 9999,
                background: C.greenBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 28,
              }}
            >
              🔧
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.ink }}>
              조건에 맞는 요청이 없어요
            </div>
            <div style={{ fontSize: 12, color: C.sub, textAlign: "center" }}>
              필터를 바꿔보거나 새 수리요청을 등록해보세요.
            </div>
          </div>
        )}

        {!initLoading && loading && <SkeletonRow />}
      </div>
    </div>
  );
}
